import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ExternalLink, Info, ArrowLeft, ClipboardList, StickyNote, Save, Dumbbell } from 'lucide-react';
import BrandLogoBar from '@/components/BrandLogoBar';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import { useIsPwaMode } from '@/hooks/useIsPwaMode';
import Navigation from '@/components/Navigation';
import SiteFooter from '@/components/website/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/utils/constants';
import { CATALOG, SECTION_META } from './WeeklyProgramPage';

const FIELD_MAP = {
  mecanique: 'mecanique_items',
  biochimie: 'biochimie_items',
  nerf_vague: 'nerf_vague_items',
  mobilite: 'mobilite_items',
  conseils: 'conseils_items',
};

const MonProgrammePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPwa = useIsPwaMode();
  // Detect whether the user came from the exercises hub (/breathing) via ?from=hub.
  // When true, we show an extra "Retour aux exercices" button so the elderly
  // patient can bounce straight back to the circles grid they came from.
  const fromHub = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('from') === 'hub';
  }, [location.search]);
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState(null);
  // Draft patient notes keyed by "sectionKey|itemId"
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/weekly-program`);
        if (cancelled) return;
        setProgram(resp.data || null);
      } catch (err) {
        console.error(err);
        toast.error('Impossible de charger votre programme');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getStoredItem = (sectionKey, id) => {
    if (!program) return null;
    const field = FIELD_MAP[sectionKey];
    return (program[field] || []).find((i) => i.id === id) || null;
  };

  // Compute displayed items: catalog items marked "required" (always) + items stored as checked in DB
  // + custom items added by the therapist (label carried directly on the stored row).
  const displayedItems = useMemo(() => {
    const out = {};
    Object.keys(FIELD_MAP).forEach((sectionKey) => {
      const catalog = CATALOG[sectionKey] || [];
      const stored = program ? (program[FIELD_MAP[sectionKey]] || []) : [];
      const rows = [];
      catalog.forEach((it) => {
        const s = stored.find((x) => x.id === it.id);
        if (it.required || (s && s.checked)) {
          rows.push({ ...it, storedNote: s?.note || '', patientNote: s?.patient_note || '' });
        }
      });
      // Append therapist-added custom items (id starts with "custom_" or flagged as custom).
      stored.forEach((s) => {
        if ((s.custom || (typeof s.id === 'string' && s.id.startsWith('custom_'))) && (s.label || s.note)) {
          rows.push({
            id: s.id,
            label: s.label || 'Consigne personnalisée',
            description: '',
            storedNote: s?.note || '',
            patientNote: s?.patient_note || '',
          });
        }
      });
      out[sectionKey] = rows;
    });
    return out;
  }, [program]);

  const totalPrescribed = useMemo(() => {
    return Object.values(displayedItems).reduce((sum, arr) => sum + arr.length, 0);
  }, [displayedItems]);

  const savePatientNote = async (sectionKey, itemId) => {
    const key = `${sectionKey}|${itemId}`;
    const note = drafts[key] ?? getStoredItem(sectionKey, itemId)?.patient_note ?? '';
    setSavingKey(key);
    try {
      await axios.patch(`${API}/weekly-program/patient-note`, {
        section: sectionKey,
        item_id: itemId,
        patient_note: note,
      });
      // Refresh local program state so displayed patientNote matches.
      setProgram((prev) => {
        if (!prev) return prev;
        const field = FIELD_MAP[sectionKey];
        const items = [...(prev[field] || [])];
        const idx = items.findIndex((i) => i.id === itemId);
        const clean = note ? note.trim() : '';
        if (idx >= 0) {
          items[idx] = { ...items[idx], patient_note: clean || null };
        } else {
          items.push({ id: itemId, checked: false, note: null, patient_note: clean || null });
        }
        return { ...prev, [field]: items };
      });
      setDrafts((d) => { const nd = { ...d }; delete nd[key]; return nd; });
      toast.success('Note personnelle enregistrée');
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'enregistrer la note");
    } finally {
      setSavingKey(null);
    }
  };

  const renderSection = (sectionKey) => {
    const meta = SECTION_META[sectionKey];
    const rows = displayedItems[sectionKey] || [];
    if (rows.length === 0) return null;
    const Icon = meta.icon;
    return (
      <section key={sectionKey} className={`rounded-2xl border-2 ${meta.border} ${meta.bg} p-4 sm:p-6 shadow-sm`} data-testid={`mon-programme-section-${sectionKey}`}>
        <header className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${meta.gradient} shadow-md`}>
            <Icon size={20} className="text-white" strokeWidth={2.2} />
          </div>
          <h2 className={`text-xl sm:text-2xl font-semibold ${meta.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {meta.title}
          </h2>
        </header>
        <ul className="space-y-2">
          {rows.map((it) => {
            const key = `${sectionKey}|${it.id}`;
            const draft = drafts[key];
            const patientNote = draft ?? it.patientNote ?? '';
            const dirty = draft !== undefined && draft !== (it.patientNote || '');
            return (
              <li key={it.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${meta.gradient} text-white shadow`}>
                    <CheckCircle2 size={16} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <p className="font-medium text-slate-900">
                        {it.label}
                        {it.required && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-sky-800 bg-sky-100 border border-sky-200 rounded-full px-2 py-0.5 align-middle">
                            Priorité
                          </span>
                        )}
                      </p>
                      {(it.path || it.href) && (
                        <button
                          type="button"
                          onClick={() => {
                            if (it.href) window.open(it.href, '_blank', 'noopener,noreferrer');
                            else navigate(it.path);
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 rounded-full px-3 py-1.5 shadow-sm transition"
                          data-testid={`mon-programme-launch-${it.id}`}
                        >
                          <ExternalLink size={12} />
                          Commencer
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>
                    {it.storedNote && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                        <StickyNote size={13} className="text-sky-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Consigne du thérapeute</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{it.storedNote}</p>
                        </div>
                      </div>
                    )}
                    <div className="mt-2">
                      <label className="block text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-1">Ma note personnelle</label>
                      <div className="flex items-start gap-2">
                        <textarea
                          rows={2}
                          value={patientNote}
                          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value.slice(0, 1000) }))}
                          placeholder="Ressenti, difficulté, question à poser…"
                          className="flex-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
                          data-testid={`mon-programme-patient-note-${it.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => savePatientNote(sectionKey, it.id)}
                          disabled={savingKey === key || !dirty}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          data-testid={`mon-programme-patient-note-save-${it.id}`}
                        >
                          <Save size={12} />
                          {savingKey === key ? '…' : (dirty ? 'Enregistrer' : 'À jour')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50">
      {!isPwa && <SiteHeaderV2 />}

      <main className={`${isPwa ? 'pt-8' : 'pt-24'} pb-16 max-w-4xl mx-auto px-4 sm:px-6`} data-testid="mon-programme-page">
        <BrandLogoBar className="mb-4" />
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button
              type="button"
              onClick={() => navigate('/mon-espace')}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
              data-testid="mon-programme-back-espace"
            >
              <ArrowLeft size={14} /> Retour à Mon espace
            </button>
            {fromHub && (
              <button
                type="button"
                onClick={() => navigate('/breathing')}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-sky-800 hover:bg-sky-200 transition"
                data-testid="mon-programme-back-hub"
              >
                <ArrowLeft size={14} /> Retour aux exercices
              </button>
            )}
          </div>
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <ClipboardList size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Mon programme
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Bonjour {user?.name?.split(' ')?.[0] || ''} — voici les exercices et conseils prescrits par votre physiothérapeute. Vous pouvez ajouter vos propres notes en dessous de chacun d&apos;eux.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-sm text-emerald-800">
              <CheckCircle2 size={14} /> {totalPrescribed} élément{totalPrescribed > 1 ? 's' : ''} au programme
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-200 text-sm text-amber-800">
              <Info size={14} /> Cliquez « Commencer » pour lancer un exercice
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Chargement…</div>
        ) : totalPrescribed === 0 && !program?.reinforcement?.enabled && !program?.therapist_notes && !program?.bilan_mecanique && !program?.bilan_biochimique?.explication && !program?.bilan_nerveux?.status ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center">
            <ClipboardList size={32} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 font-medium">Aucun programme n&apos;a encore été créé.</p>
            <p className="text-sm text-slate-500 mt-1">
              Lors de votre prochaine séance, votre physiothérapeute co-créera le programme avec vous.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Bilan synthesis (read-only, patient view) */}
            {(program?.bilan_biochimique || program?.bilan_mecanique || program?.bilan_nerveux) && (() => {
              const bio = program.bilan_biochimique || {};
              const hasBio = bio.bolt || bio.inspir_max || bio.expir_max || bio.apnea_inspir || bio.mbt || bio.explication;
              const meca = (program.bilan_mecanique || '').trim();
              const nerv = program.bilan_nerveux || {};
              const hasNerv = nerv.status || nerv.notes;
              if (!hasBio && !meca && !hasNerv) return null;
              const NERVEUX_LABELS = {
                equilibre: { label: 'Équilibré', desc: 'Bon tonus vagal, régulation efficace', color: 'text-emerald-700 bg-emerald-100 border-emerald-200' },
                stress_fuite_combat: { label: 'Stress · fuite / combat', desc: 'Sympathique dominant', color: 'text-rose-700 bg-rose-100 border-rose-200' },
                stress_inhibition: { label: 'Stress · inhibition / immobilisation', desc: 'Vague dorsal dominant', color: 'text-slate-700 bg-slate-100 border-slate-200' },
              };
              const nervPill = nerv.status ? NERVEUX_LABELS[nerv.status] : null;
              return (
                <section className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-4 sm:p-6 shadow-sm">
                  <header className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-blue-600 shadow-md">
                      <Info size={20} className="text-white" strokeWidth={2.2} />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-indigo-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Synthèse du bilan
                    </h2>
                  </header>

                  {hasBio && (
                    <div className="bg-white rounded-xl border border-indigo-200 p-4 mb-3">
                      <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide mb-3">1. Aspect biochimique</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                        {[
                          { key: 'bolt', label: 'BOLT', unit: 's' },
                          { key: 'inspir_max', label: 'Inspir max', unit: 's' },
                          { key: 'expir_max', label: 'Expir max', unit: 's' },
                          { key: 'apnea_inspir', label: 'Apnée inspir', unit: 's' },
                          { key: 'mbt', label: 'MBT', unit: 'pas' },
                        ].filter((f) => bio[f.key]).map((f) => (
                          <div key={f.key} className="rounded-lg bg-indigo-50 border border-indigo-100 px-2 py-2 text-center">
                            <p className="text-[9px] font-bold text-indigo-700 uppercase tracking-wide">{f.label}</p>
                            <p className="text-lg font-bold text-indigo-900 leading-tight">{bio[f.key]}<span className="text-[10px] font-normal text-indigo-600 ml-0.5">{f.unit}</span></p>
                          </div>
                        ))}
                      </div>
                      {bio.explication && (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {bio.explication}
                        </p>
                      )}
                    </div>
                  )}

                  {meca && (
                    <div className="bg-white rounded-xl border border-indigo-200 p-4 mb-3">
                      <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide mb-2">2. Aspect mécanique</h3>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{meca}</p>
                    </div>
                  )}

                  {hasNerv && (
                    <div className="bg-white rounded-xl border border-indigo-200 p-4">
                      <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide mb-2">3. Système nerveux</h3>
                      {nervPill && (
                        <div className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold border ${nervPill.color} mb-2`}>
                          {nervPill.label}
                        </div>
                      )}
                      {nervPill && (
                        <p className="text-xs text-slate-500 mb-2">{nervPill.desc}</p>
                      )}
                      {nerv.notes && (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{nerv.notes}</p>
                      )}
                    </div>
                  )}
                </section>
              );
            })()}
            {['mecanique', 'biochimie', 'nerf_vague', 'mobilite', 'conseils'].map(renderSection)}

            {/* Muscle reinforcement (read-only) */}
            {program?.reinforcement?.enabled && (
              <section className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 sm:p-6 shadow-sm">
                <header className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 shadow-md">
                    <Dumbbell size={20} className="text-white" strokeWidth={2.2} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-rose-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Renforcement de la musculature respiratoire
                  </h2>
                </header>
                <div className="bg-white rounded-xl p-4 border border-rose-200 space-y-2">
                  <p className="text-slate-800 font-medium">
                    {program.reinforcement.reps_per_day} répétitions par jour avec l&apos;appareil de renforcement respiratoire
                  </p>
                  {program.reinforcement.notes && (
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{program.reinforcement.notes}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate('/entrainement-muscles-respiratoires')}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:text-rose-900"
                  >
                    <ExternalLink size={12} /> En savoir plus sur l&apos;entraînement des muscles respiratoires
                  </button>
                </div>
              </section>
            )}

            {/* Therapist notes (read-only) */}
            {program?.therapist_notes && (
              <section className="rounded-2xl border-2 border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <header className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-500 to-slate-700 shadow-md">
                    <StickyNote size={20} className="text-white" strokeWidth={2.2} />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Notes du thérapeute
                  </h2>
                </header>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {program.therapist_notes}
                </p>
              </section>
            )}
          </div>
        )}
      </main>

      {isPwa ? (
        <>
          <div style={{ height: 'calc(env(safe-area-inset-bottom,0px) + 100px)' }} aria-hidden="true" />
          <Navigation />
        </>
      ) : (
        <SiteFooter />
      )}
    </div>
  );
};

export default MonProgrammePage;
